import React, { useState } from 'react'
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
  Typography
} from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { FacebookOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons'
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred'
import { Flex, message, Table } from 'antd'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import DeleteIcon from '@mui/icons-material/Delete'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import AddIcon from '@mui/icons-material/Add'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

import SelectSearch, { IOptionSelect } from '../../../../../components/SelectSearch'
import { IAddress, IAddressThailand } from '../../../../../types/address'
import { IDistributionChannel } from '../../../../../types/distribution-channel'
import { IUserLists } from '../../../../../types/user'
import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { OrderUtil } from '../../../../../utils/order.util'
import { IProduct } from '../../../../../types/product'
import InputUpload from '../../../../../components/InputUpload'
import { OrderFormAddProductModal } from '../../../../Order/Features/OrderList/components/OrderFormAddProductModal'
import { validateMinMaxValue } from '../../../../../utils/validateMinMaxValue'
import { useDispatch } from 'react-redux'
import { closeModal, openModal } from '../../../../../../store/reducers/modal/modal.slice'
import ConfirmOrderModal from '../../../../../components/ConfirmOrderModal'
import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'

// interface FacebookOrderDrawerProps {
//   isOpen: boolean;
//   onClose: () => void;
// }

interface IProductsForm {
  product?: IProduct
  quantity?: number
  discount?: number
}

interface IOrderForm {
  name: string
  phone: string
  address: string
  subDistrict: string
  district: string
  province: string
  zipcode: string
  facebook: string
  isCOD: number
  products: IProductsForm[]
  orderDate: string
  paymentDate: string
  distribution: string
  paymentUrl: string
  note: string
  commissionId: string
  telesaleId: string
}

const getOrderFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string().required('กรุณากรอกชื่อ-นามสกุล'),
    phone: Yup.string().required('กรุณากรอกหมายเลขโทรศัพท์'),
    address: Yup.string().required('กรุณากรอกที่อยู่'),
    subDistrict: Yup.string().required('กรุณากรอกตำบล'),
    district: Yup.string().required('กรุณากรอกอำเภอ'),
    province: Yup.string().required('กรุณากรอกจังหวัด'),
    zipcode: Yup.string().required('กรุณากรอกรหัสไปรษณีย์'),
    facebook: Yup.string().required('กรุณากรอก Facebook'),
    orderDate: Yup.string().required('กรุณาเลือกวันที่สั่งซื้อ'),
    products: Yup.array(
      Yup.object({
        product: Yup.object().required('กรุณาเลือกสินค้า'),
        quantity: Yup.number().required('กรุณากรอกจำนวนสินค้า'),
        discount: Yup.number().min(0, 'กรุณากรอกส่วนลดให้ถูกต้อง'),
      }),
    ).min(1, 'กรุณาเลือกสินค้าอย่างน้อย 1 รายการ'),
    isCOD: Yup.number().required(),
    paymentDate: Yup.string(),
    distribution: Yup.string().required('กรุณาเลือกช่องทางการขาย'),
    paymentUrl: Yup.string(),
    note: Yup.string(),
    commissionId: Yup.string(),
    telesaleId: Yup.string(),
  })
}

const FacebookOrder = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [searchAddress, setSearchAddress] = useState<string>('')
  const [searchUserCommission, setSearchUserCommission] = useState<string>('')
  const [searchUserTelesales, setSearchUserTelesales] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] = useState<string>('')
  const [isModalAddProduct, setIsModalAddProduct] = useState<boolean>(false)
  const [formatAddress, setFormatAddress] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const dispatch = useDispatch()

  const handleCheckCustomerWarning = async (values: IOrderForm) => {
    try {
      const responseCustomerWarning = await axiosInstance.post('/blacklists/checking', {
        name: values.name,
        phoneNumber: values.phone,
      })
      if (responseCustomerWarning.data.blacklist) {
        dispatch(openModal({
          title: `เตือน! ลูกค้า ${values.name} อยู่ในรายการ Blacklist`,
          content: <ConfirmOrderModal
            onConfirm={async () => {
              await formik.handleSubmit()
              dispatch(closeModal())
            }}
          />
        }))
      } else {
        await formik.handleSubmit()
      }
    } catch (e: any) {
      const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }
  const formik = useFormik<IOrderForm>({
    initialValues: {
      name: '',
      phone: '',
      address: '',
      subDistrict: '',
      district: '',
      province: '',
      zipcode: '',
      facebook: '',
      isCOD: 1,
      products: [],
      orderDate: '',
      distribution: '',
      paymentDate: '',
      paymentUrl: '',
      note: '',
      commissionId: '',
      telesaleId: '',
    } as IOrderForm,
    validationSchema: getOrderFormValidationSchema(),
    onSubmit: async (values: IOrderForm) => {
      setIsLoading(true)
      try {
        await axiosInstance.post(
          `/order`,
          {
            name: values.name.trim() as string,
            phone: values.phone.trim() as string,
            address: {
              address: values.address.trim() as string,
              subDistrict: values.subDistrict.trim() as string,
              district: values.district.trim() as string,
              province: values.province.trim() as string,
              zipcode: values.zipcode.trim() as string,
            },
            facebook: values.facebook.trim() as string,
            isCOD: !!values.isCOD,
            products: values.products.map((product) => ({
              objectId: product.product?.objectId,
              quantity: product.quantity,
              discount: product.discount,
            })),
            orderDate: values.orderDate,
            distribution: values.distribution,
            paymentDate: values.paymentDate,
            paymentUrl: values.paymentUrl,
            note: values.note,
          },
        )
        formik.resetForm()
        message.success('สร้างสินค้าสำเร็จ')
      } catch (e: any) {
        const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const addressList = useAxios<IAddressThailand[]>({
    method: 'GET',
    url: '/address/thailand-address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate()
  }

  const userCommissionList = useAxios<PaginationResponseInterface<IUserLists>>({
    method: 'GET',
    url: '/users/merchant',
    params: {
      page: 1,
      perPage: 5,
      search: searchUserCommission,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchUserCommission = async (value: string) => {
    setSearchUserCommission(value)
    await userCommissionList.mutate()
  }

  const userTelesalesList = useAxios<PaginationResponseInterface<IUserLists>>({
    method: 'GET',
    url: '/users/merchant',
    params: {
      page: 1,
      perPage: 5,
      search: searchUserTelesales,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchUserTelesales = async (value: string) => {
    setSearchUserTelesales(value)
    await userTelesalesList.mutate()
  }

  const distributionChannels = useAxios<PaginationResponseInterface<IDistributionChannel>>({
    method: 'GET', url: '/distribution-channels', params: {
      page: 1, perPage: 5, name: searchDistributionChannel, startDate: '', endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    await distributionChannels.mutate()
  }

  const formatAddressSubmit = async () => {
    try {
      const message = await OrderUtil.extractAddress(formatAddress)
      const { name, phone, other, tambon, amphoe, province, postcode } =
        message
      handleSelectAddress({
        name: name.text,
        phoneNumber: phone[0].text,
        address: other,
        subDistrict: tambon.text,
        district: OrderUtil.amphurMueang(amphoe.text, province.text),
        province: province.text,
        postcode: postcode.text,
      } as IAddress)
    } catch (e) {
      return
    }
  }

  const handleSelectAddress = (address?: IAddress) => {
    formik.setFieldValue('name', address?.name ?? '')
    formik.setFieldValue('phone', address?.phoneNumber ?? '')
    formik.setFieldValue('address', address?.address ?? '')
    formik.setFieldValue('subDistrict', address?.subDistrict ?? '')
    formik.setFieldValue('district', address?.district ?? '')
    formik.setFieldValue('province', address?.province ?? '')
    formik.setFieldValue('zipcode', address?.postcode ?? '')
  }

  const getTotal = () => {
    return formik.values.products.reduce(
      (total: number, product: IProductsForm) => total + ((product.product?.price! * product.quantity!) - product.discount! || 0),
      0,
    )
  }

  const handleUploadClick = async (event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      await formik.setFieldValue('paymentUrl', response.data.url)
      return message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const handleDeleteCollectionByIndex = (index: number) => {
    const prevCollections = formik.values.products
    formik.setFieldValue('products',
      prevCollections.filter((_, i) => i !== index),
    )
  }

  const handleAddCollection = (value: IProductsForm) => {
    const prevCollections = formik.values.products
    formik.setFieldValue('products', [...prevCollections, value])
  }

  const onChangeDiscount = (index: number, value: any) => {
    const prevCollections = formik.values.products
    prevCollections[index].discount = value
    formik.setFieldValue('products', prevCollections)
  }

  const columns = [
    {
      title: 'รายการสินค้า',
      dataIndex: 'product',
      key: 'name',
      width: 220,
      render: (product: IProduct) => {
        return (
          <Flex
            vertical
          >
            <Typography variant="subtitle1" color="text.secondary">
              {product?.name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {product?.code}
            </Typography>
          </Flex>
        )
      },
    },
    {
      title: 'ราคาต่อชิ้น',
      dataIndex: 'product',
      key: 'price',
      width: 100,
      render: (product: IProduct) =>
        <Typography variant="subtitle2" color="text.secondary">
          {(product?.price || 0).toLocaleString()}
        </Typography>,
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 150,
      render: (quantity: number) =>
        <Typography variant="subtitle2" color="text.secondary">
          {(quantity || 0).toLocaleString()}
        </Typography>,
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'key',
      key: 'discount',
      width: 100,
      render: (key?: number) =>
        <TextField
          fullWidth
          label="ส่วนลด"
          name="discount"
          value={formik.values.products[key!].discount || ""}
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*', '.'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onChange={
            (e) => {
              onChangeDiscount(key!, validateMinMaxValue(e.target.value, 0, formik.values.products[key!].product!.price || 1) || '')
            }
          }
          helperText={
            formik.touched.products && formik.errors.products as string
          }
          error={
            !!(formik.touched.products && formik.errors.products)
          }
        />
    },
    {
      title: 'ราคารวม',
      dataIndex: 'product',
      key: 'total',
      width: 100,
      render: (_: undefined, record: IProductsForm, index: number) => (
        <Typography variant="subtitle2" color="text.secondary">
          {((record.product!.price * record.quantity!) - record.discount! || 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      dataIndex: 'key',
      key: 'delete',
      width: 100,
      render: (key: number) =>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <IconButton
              onClick={() => handleDeleteCollectionByIndex(key)}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>,
    },
  ]

  return (
    <>
      <OrderFormAddProductModal
        onAdd={handleAddCollection}
        isOpen={isModalAddProduct}
        onClose={(visible) => setIsModalAddProduct(visible)}
      />
      {/* <Drawer
        anchor={'right'}
        open={isOpen}
        onClose={onClose}
        PaperProps={{ style: { width: isMobile ? '80%' : '40%', maxWidth: isMobile ? '80%' : '40%' } }}
      > */}
      <Box>
        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid item xs={12} lg={12}>
            <Typography variant={'h6'}>รายการคำสั่งซื้อใหม่</Typography>
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              fullWidth
              label={`ตัวช่วยจำแนกที่อยู่`}
              multiline
              placeholder={`ตัวอย่างการกรอกข้อมูล
คุณมานี มานะ
0831725655
69/429 หมู่ 2 ต.ช้างเผือก อ.เมืองเชียงใหม่ จ.เชียงใหม่ 50300`}
              rows={5}
              onChange={(e) => setFormatAddress(e.target.value)}
            />
            <Typography mt={1} variant={'body2'} color={'error'}>
              {' '}
              ** สามารถใช้คำเต็มหรือตัวย่อได้ เช่น ตำบล/ต. ,
              อำเภอ/อ. หรือ จังหวัด/จ. **{' '}
            </Typography>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Button
              fullWidth
              startIcon={<AutoFixHighIcon />}
              color="primary"
              size="medium"
              variant="contained"
              onClick={() => formatAddressSubmit()}
            >
              นำเข้าที่อยู่
            </Button>
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              id="name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <UserOutlined />
                  </InputAdornment>
                ),
              }}
              fullWidth
              label={`ชื่อ-นามสกุล`}
              name="name"
              helperText={
                formik.touched.name && formik.errors.name as string
              }
              error={
                !!(formik.touched.name && formik.errors.name)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              id="phone"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlined />
                  </InputAdornment>
                ),
              }}
              fullWidth
              label={`หมายเลขโทรศัพท์`}
              name="phone"
              placeholder={`ตัวอย่างการกรอกข้อมูล 0831725655`}
              helperText={
                formik.touched.phone && formik.errors.phone as string
              }
              error={
                !!(formik.touched.phone && formik.errors.phone)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.phone}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              id={'address'}
              multiline
              fullWidth
              label={`ที่อยู่`}
              name="address"
              rows={5}
              placeholder={`บ้านเลขที่, หมู่ ชั้นหรือซอย `}
              helperText={
                formik.touched.address && formik.errors.address as string
              }
              error={
                !!(formik.touched.address && formik.errors.address)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.address}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <SelectSearch<IAddressThailand>
              label={'ค้นหาที่อยู่'}
              onSelect={(e) => {
                formik.setFieldValue('subDistrict', e?.value.subDistrict)
                formik.setFieldValue('district', e?.value.district)
                formik.setFieldValue('province', e?.value.province)
                formik.setFieldValue('zipcode', e?.value.zipcode)
              }}
              search={searchAddress}
              onSearch={handleSearchAddress}
              options={
                addressList.data?.map((address: IAddressThailand) => ({
                  label: `${address.subDistrict} ${address.district} ${address.province} ${address.zipcode}`,
                  value: address,
                })) || [] as IOptionSelect<IAddressThailand>[]
              } />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="province"
              fullWidth
              label="จังหวัด"
              name="province"
              helperText={
                formik.touched.province && formik.errors.province as string
              }
              error={
                !!(formik.touched.province && formik.errors.province)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.province}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="district"
              fullWidth
              label="อำเภอ"
              name="district"
              helperText={
                formik.touched.district && formik.errors.district as string
              }
              error={
                !!(formik.touched.district && formik.errors.district)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.district}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="subDistrict"
              fullWidth
              label="ตำบล"
              name="subDistrict"
              helperText={
                formik.touched.subDistrict && formik.errors.subDistrict as string
              }
              error={
                !!(formik.touched.subDistrict && formik.errors.subDistrict)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.subDistrict}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              id="zipcode"
              fullWidth
              label="รหัสไปรษณีย์"
              name="zipcode"
              helperText={
                formik.touched.zipcode && formik.errors.zipcode as string
              }
              error={
                !!(formik.touched.zipcode && formik.errors.zipcode)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.zipcode}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FacebookOutlined />
                  </InputAdornment>
                ),
              }}
              fullWidth
              label={`Facebook`}
              name="facebook"
              helperText={
                formik.touched.facebook && formik.errors.facebook as string
              }
              error={
                !!(formik.touched.facebook && formik.errors.facebook)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.facebook}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <SelectSearch<IDistributionChannel>
              label={'เลือกช่องทางการขาย'}
              onSelect={(e) => formik.setFieldValue('distribution', e?.value.objectId)}
              search={searchDistributionChannel}
              onSearch={handleSearchDistributionChannel}
              options={
                distributionChannels.data?.records.map((distributionChannel: IDistributionChannel) => ({
                  label: distributionChannel.name,
                  value: distributionChannel,
                })) || [] as IOptionSelect<IDistributionChannel>[]
              } />
          </Grid>
          <Grid item xs={12} lg={12}>
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReportGmailerrorredIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
              label={`หมายเหตุ`}
              name="note"
              helperText={
                formik.touched.note && formik.errors.note as string
              }
              error={
                !!(formik.touched.note && formik.errors.note)
              }
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.note}
            />
          </Grid>

          <Grid item xs={12} lg={12}>
            <Grid container spacing={2} justifyContent={'space-between'} alignItems={'center'}>
              <Grid item>
                <Typography variant={'h6'}>รายการสินค้า</Typography>
              </Grid>
              <Grid item>
                <Button
                  startIcon={<AddIcon />}
                  color="warning"
                  size="medium"
                  variant="contained"
                  onClick={() => setIsModalAddProduct(!isModalAddProduct)}
                >
                  เพิ่มสินค้า
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Table
              size={isMobile ? 'small' : 'middle'}
              columns={columns}
              dataSource={formik.values.products?.map((product: IProductsForm, index: number) => {
                return {
                  ...product,
                  key: index,
                }
              })}
              bordered
              pagination={{ hideOnSinglePage: true }}
            />
          </Grid>
          <Grid container justifyContent={'flex-end'} mt={2}>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                ราคารวม: {getTotal().toLocaleString()} บาท
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={6}>
            <SelectSearch<IUserLists>
              label={'ลงออเดอร์ (ค่าคอม) ให้พนักงาน'}
              onSelect={(e) => formik.setFieldValue('commissionId', e?.value.objectId)}
              search={searchUserCommission}
              onSearch={handleSearchUserCommission}
              options={
                // filtter isAvailable === true will show in options
                userCommissionList.data?.records.filter((userCommission: IUserLists) => userCommission.isAvailable)
                  .map((userCommission: IUserLists) => ({
                    label: userCommission.name,
                    value: userCommission,
                  })) || [] as IOptionSelect<IUserLists>[]
              } />
          </Grid>
          <Grid item xs={12} lg={6}>
            <SelectSearch<IUserLists>
              label={'เลือกผู้ดูแล Telesale (กรุณาเว้นว่างไว้ หากไม่ต้องการกำหนด)'}
              onSelect={(e) => formik.setFieldValue('telesaleId', e?.value.objectId)}
              search={searchUserTelesales}
              onSearch={handleSearchUserTelesales}
              options={
                userTelesalesList.data?.records.filter((userTelesales: IUserLists) => userTelesales.isAvailable)
                  .map((userTelesales: IUserLists) => ({
                    label: userTelesales.name,
                    value: userTelesales,
                  })) || [] as IOptionSelect<IUserLists>[]
              } />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Select
              id="isCOD"
              name="isCOD"
              placeholder="การชำระเงิน"
              value={formik.values.isCOD}
              onChange={formik.handleChange}
              fullWidth
            >
              <MenuItem value={1}>เก็บเงินปลายทาง</MenuItem>
              <MenuItem value={0}>ชำระเงินผ่านธนาคาร</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} lg={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format={ONLY_DATE_FORMAT}
                disableFuture
                label="วันที่สั่งซื้อ"
                name="orderDate"
                value={formik.values.orderDate ? dayjs(formik.values.orderDate) : null}
                onChange={(e) => {
                  formik.setFieldValue('orderDate', e?.format())
                }}
                sx={{ width: '100%' }}
                slotProps={{
                  textField: {
                    helperText: (formik.touched.orderDate && formik.errors.orderDate as string),
                    error: (!!(formik.touched.orderDate && formik.errors.orderDate)),
                    onBlur: (formik.handleBlur),
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          {formik.values.isCOD === 0 && (
            <>
              <Grid item xs={12} lg={6}>
                <InputUpload
                  onRemove={() => formik.setFieldValue('paymentUrl', '')}
                  handleUploadClick={handleUploadClick}
                  imageUrl={formik.values.paymentUrl ?? ''}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format={ONLY_DATE_FORMAT}
                    disableFuture
                    label="วันที่ชำระ"
                    name="paymentDate"
                    value={formik.values.paymentDate ? dayjs(formik.values.paymentDate) : null}
                    onChange={(e) => {
                      formik.setFieldValue('paymentDate', e?.format())
                    }}
                    sx={{ width: '100%' }}
                    slotProps={{
                      textField: {
                        helperText: (formik.touched.paymentDate && formik.errors.paymentDate as string),
                        error: (!!(formik.touched.paymentDate && formik.errors.paymentDate)),
                        onBlur: (formik.handleBlur),
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
          <Grid item xs={12} lg={12}>
            <Button
              color="primary"
              onClick={() => handleCheckCustomerWarning(formik.values)}
              size="large"
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
            >
              สร้างออเดอร์
            </Button>
          </Grid>
        </Grid>

      </Box>
      {/* </Drawer> */}
    </>
  )
}

export default FacebookOrder